import React from 'react';
import styled from 'styled-components';

import {
    FcAssistant,
    FcCalendar,
    FcCancel,
    FcCheckmark,
    FcClock,
    FcDeployment,
    FcInvite,
    FcSafe,
} from 'react-icons/fc';

import { confirmPopup } from 'components/Popups/Confirm';

import {
    SupplyOrderState,
    useMarkSupplyOrderAsCheckedMutation,
    useMarkSupplyOrderAsConfirmedMutation,
    useMarkSupplyOrderAsInrackMutation,
    useMarkSupplyOrderAsOnHoldMutation,
    useMarkSupplyOrderAsOrderedMutation,
    useMarkSupplyOrderAsReadyMutation,
    useMarkSupplyOrderAsReceivedMutation,
    useMarkSupplyOrderAsCreatedMutation,
    useUpdateManualSupplyOrderForecastsMutation,
    useUpdateManualSupplyOrderProductStockVariationsMutation,
    GetSupplyOrdersDocument,
    GetSupplyOrderSupplierProductsStockVariationDocument,
} from 'data/__generated__';

import { ActionButtons } from 'components/ActionButtons';
import { SectionContainer } from 'components/DetailsView/Section';
import { FullPageLoader } from 'components/FullPageLoader';

import { SupplyOrderDetailsDisplayValues } from './supplyOrderDetailsFormHelper';

import { computeHourDeliveryFromDate } from 'helpers/dateTimes';
import { handleUpdateActionEndToast } from './utils/handleUpdateActionEndToast';
import { handleUpdateResults } from 'handlers';

export const ActionSection = ({
    isDirty,
    setShowAbortPopup,
    supplyOrderDetailsDisplayValues,
}: {
    isDirty: boolean;
    setShowAbortPopup: React.Dispatch<React.SetStateAction<boolean>>;
    supplyOrderDetailsDisplayValues: SupplyOrderDetailsDisplayValues;
}) => {
    const { _id: supplyOrderId, state, stateHistory } = supplyOrderDetailsDisplayValues;

    const [markSupplyOrderAsChecked, { loading: loadingChecked }] = useMarkSupplyOrderAsCheckedMutation({
        variables: { supplyOrderIds: [supplyOrderId] },
    });
    const [markSupplyOrderAsConfirmed, { loading: loadingConfirmed }] = useMarkSupplyOrderAsConfirmedMutation({
        variables: { supplyOrderIds: [supplyOrderId] },
    });
    const [markSupplyOrderAsInRack, { loading: loadingInRack }] = useMarkSupplyOrderAsInrackMutation({
        variables: { supplyOrderIds: [supplyOrderId] },
    });
    const [markSupplyOrderAsOnHold, { loading: loadingOnHold }] = useMarkSupplyOrderAsOnHoldMutation({
        variables: { supplyOrderId },
    });

    const [markSupplyOrderAsOrdered, { loading: loadingOrdered }] = useMarkSupplyOrderAsOrderedMutation();
    const [markSupplyOrderAsReady, { loading: loadingReady }] = useMarkSupplyOrderAsReadyMutation({
        variables: { supplyOrderIds: [supplyOrderId] },
    });
    const [markSupplyOrderAsCreated, { loading: loadingCreated }] = useMarkSupplyOrderAsCreatedMutation({
        variables: { supplyOrderIds: [supplyOrderId] },
    });
    const [markSupplyOrderAsReceived, { loading: loadingReceived }] = useMarkSupplyOrderAsReceivedMutation();

    const [updateManualSupplyOrderProductStockVariations, { loading: updateStockVariationsLoading }] =
        useUpdateManualSupplyOrderProductStockVariationsMutation({
            refetchQueries: [GetSupplyOrderSupplierProductsStockVariationDocument],
            variables: { supplyOrderIds: [supplyOrderId] },
        });
    const [updateManualSupplyOrderForecasts, { loading: updateForecastsLoading }] =
        useUpdateManualSupplyOrderForecastsMutation({
            refetchQueries: [GetSupplyOrdersDocument],
            variables: { supplyOrderIds: [supplyOrderId] },
        });

    const loading =
        loadingCreated ||
        loadingReady ||
        loadingOrdered ||
        loadingConfirmed ||
        loadingOnHold ||
        loadingReceived ||
        loadingChecked ||
        loadingInRack ||
        updateStockVariationsLoading ||
        updateForecastsLoading;

    async function excecuteActionOnConfirm(action: () => Promise<void>, confirmText: string) {
        const confirmed = await confirmPopup({ title: 'Confirmer', message: confirmText });
        if (confirmed) {
            await action();
        }
    }

    const wasAlreadyOrdered = stateHistory.some(({ state }) => state === SupplyOrderState.Ordered);

    const actionSections = [
        {
            title: 'Changer le statut',
            actions: [
                {
                    icon: <FcInvite size={22} />,
                    label: 'Créé',
                    onClick: async () => {
                        await excecuteActionOnConfirm(async () => {
                            const { data } = await markSupplyOrderAsCreated();

                            if (!data) {
                                throw new Error("Une erreur est survenue lors du passage de l'achat en 'créé'");
                            }

                            const {
                                markSupplyOrdersAsCreated: { updatedSupplyOrders, errors, warnings },
                            } = data;
                            const isSuccess = !!updatedSupplyOrders.length;
                            handleUpdateActionEndToast({
                                displaySuccess: isSuccess,
                                successText: "L'achat a bien été marqué comme 'créé'",
                                errors,
                                warnings,
                            });
                        }, "Souhaitez-vous vraiment marquer l'achat comme 'créé' ?");
                    },
                    disabled: state !== SupplyOrderState.Ready,
                },
                {
                    icon: <FcInvite size={22} />,
                    label: 'À envoyer',
                    onClick: async () => {
                        await excecuteActionOnConfirm(async () => {
                            const { data } = await markSupplyOrderAsReady();

                            if (!data) {
                                throw new Error("Une erreur est survenue lors du passage de l'achat en 'à envoyer'");
                            }

                            const {
                                markSupplyOrdersAsReady: { updatedSupplyOrders, errors, warnings },
                            } = data;
                            const isSuccess = !!updatedSupplyOrders.length;
                            handleUpdateActionEndToast({
                                displaySuccess: isSuccess,
                                successText: "L'achat a bien été marqué comme 'à envoyer'",
                                errors,
                                warnings,
                            });
                        }, "Souhaitez-vous vraiment marquer l'achat comme 'à envoyer' ?");
                    },
                    disabled: state !== SupplyOrderState.Created,
                },
                {
                    icon: <FcAssistant size={22} />,
                    label: 'Commandé',
                    onClick: async () => {
                        await excecuteActionOnConfirm(
                            async () => {
                                const { data } = await markSupplyOrderAsOrdered({
                                    variables: { supplyOrderIds: [supplyOrderId], shouldSendMailToSupplier: true },
                                });

                                if (!data) {
                                    throw new Error("Une erreur est survenue lors du passage de l'achat en 'commandé'");
                                }

                                const {
                                    markSupplyOrdersAsOrdered: { updatedSupplyOrders, errors },
                                } = data;
                                handleUpdateActionEndToast({
                                    displaySuccess: !!updatedSupplyOrders.length,
                                    successText: "L'achat a bien été marqué comme 'commandé'",
                                    errors,
                                });
                            },
                            `Souhaitez-vous vraiment marquer l'achat comme 'commandé' ?\n\n${wasAlreadyOrdered ? "Aucun mail ne sera envoyé au fournisseur car cette commande était déjà en 'Commandé' avant" : 'Attention ! \nUn mail sera envoyé au fournisseur avec le bon de commande généré sur le google drive.'}`,
                        );
                    },
                    disabled: ![
                        SupplyOrderState.Ready,
                        SupplyOrderState.Confirmed,
                        SupplyOrderState.OnHold,
                        SupplyOrderState.Received,
                    ].includes(state),
                },
                {
                    icon: <FcCalendar size={22} />,
                    label: 'Confirmé',
                    onClick: async () => {
                        await excecuteActionOnConfirm(async () => {
                            const { data } = await markSupplyOrderAsConfirmed();

                            if (!data) {
                                throw new Error("Une erreur est survenue lors du passage de l'achat en 'confirmé'");
                            }

                            const {
                                markSupplyOrdersAsConfirmed: { updatedSupplyOrders, errors },
                            } = data;
                            handleUpdateActionEndToast({
                                displaySuccess: !!updatedSupplyOrders.length,
                                successText: "L'achat a bien été marqué comme 'confirmé'",
                                errors,
                            });
                        }, "Souhaitez-vous vraiment marquer l'achat comme 'confirmé' ?");
                    },
                    disabled: ![SupplyOrderState.Ordered, SupplyOrderState.OnHold, SupplyOrderState.Received].includes(
                        state,
                    ),
                },
                {
                    icon: <FcClock size={22} />,
                    label: 'En Attente',
                    onClick: async () => {
                        await excecuteActionOnConfirm(async () => {
                            const { data } = await markSupplyOrderAsOnHold();

                            if (!data) {
                                throw new Error("Une erreur est survenue lors du passage de l'achat en 'En Attente'");
                            }

                            const { markSupplyOrderAsOnHold: updatedSupplyOrder } = data;
                            handleUpdateActionEndToast({
                                displaySuccess: !!updatedSupplyOrder,
                                successText: "L'achat a bien été marqué comme 'En Attente'",
                                errors: [],
                            });
                        }, "Souhaitez-vous vraiment marquer l'achat comme 'En Attente' ?");
                    },
                    disabled: ![
                        SupplyOrderState.Ordered,
                        SupplyOrderState.Confirmed,
                        SupplyOrderState.Received,
                    ].includes(state),
                },
                {
                    icon: <FcDeployment size={22} />,
                    label: 'Reçu',
                    onClick: async () => {
                        await excecuteActionOnConfirm(async () => {
                            const hourDeliveryReceivedAt = computeHourDeliveryFromDate(new Date());
                            const { data } = await markSupplyOrderAsReceived({
                                variables: { supplyOrderIds: [supplyOrderId], hourDeliveryReceivedAt },
                            });

                            if (!data) {
                                throw new Error("Une erreur est survenue lors du passage de l'achat en 'reçu'");
                            }

                            const {
                                markSupplyOrdersAsReceived: { updatedSupplyOrders, errors, warnings },
                            } = data;

                            const isSuccess = !!updatedSupplyOrders.length;
                            handleUpdateActionEndToast({
                                displaySuccess: isSuccess,
                                successText: "L'achat a bien été marqué comme 'reçu'",
                                errors,
                                warnings,
                            });
                        }, "Souhaitez-vous vraiment marquer l'achat comme 'reçu' ?");
                    },
                    disabled: ![
                        SupplyOrderState.Ordered,
                        SupplyOrderState.Confirmed,
                        SupplyOrderState.OnHold,
                        SupplyOrderState.Checked,
                        SupplyOrderState.OnHold,
                    ].includes(state),
                },
                {
                    icon: <FcCheckmark size={22} />,
                    label: 'Vérifié',
                    onClick: async () => {
                        await excecuteActionOnConfirm(async () => {
                            const { data } = await markSupplyOrderAsChecked();

                            if (!data) {
                                throw new Error("Une erreur est survenue lors du passage de l'achat en 'vérifié'");
                            }

                            const {
                                markSupplyOrdersAsChecked: { updatedSupplyOrders, errors, warnings },
                            } = data;
                            const isSuccess = !!updatedSupplyOrders.length;
                            handleUpdateActionEndToast({
                                displaySuccess: isSuccess,
                                successText: "L'achat a bien été marqué comme 'vérifié'",
                                errors,
                                warnings,
                            });
                        }, "Souhaitez-vous vraiment marquer l'achat comme 'vérifié' ? \n\nAttention ! \nLes quantitées reçues non renseignées seront considérées comme étant celles confirmées par fournisseur (si renseigné) ou commandées.");
                    },
                    disabled: state !== SupplyOrderState.Received,
                },
                {
                    icon: <FcSafe size={22} />,
                    label: 'Rangé',
                    onClick: async () => {
                        await excecuteActionOnConfirm(async () => {
                            const { data } = await markSupplyOrderAsInRack();

                            if (!data) {
                                throw new Error("Une erreur est survenue lors du passage de l'achat en 'rangé'");
                            }

                            const {
                                markSupplyOrdersAsInRack: { updatedSupplyOrders, errors, warnings },
                            } = data;
                            const isSuccess = !!updatedSupplyOrders.length;
                            handleUpdateActionEndToast({
                                displaySuccess: isSuccess,
                                successText: "L'achat a bien été marqué comme 'rangé'",
                                errors,
                                warnings,
                            });
                        }, "Souhaitez-vous vraiment marquer l'achat comme 'rangé' ? \n\nAttention ! \nLe stock sera mis à jour avec les quantitées renseignées comme reçues. \nL'achat ne sera plus rangeable sur la totops.");
                    },
                    disabled: state !== SupplyOrderState.Checked,
                },
                {
                    icon: <FcCancel size={22} />,
                    label: 'Annulé',
                    onClick: async () => {
                        setShowAbortPopup(true);
                    },
                    disabled: ![
                        SupplyOrderState.Created,
                        SupplyOrderState.Ready,
                        SupplyOrderState.Ordered,
                        SupplyOrderState.Confirmed,
                        SupplyOrderState.OnHold,
                    ].includes(state),
                },
            ],
        },
        {
            title: 'Cas particuliers',
            actions: [
                {
                    icon: <FcAssistant size={22} />,
                    label: "Passer l'achat en 'Commandé' sans envoyer le mail",
                    onClick: async () => {
                        await excecuteActionOnConfirm(async () => {
                            const { data } = await markSupplyOrderAsOrdered({
                                variables: { supplyOrderIds: [supplyOrderId], shouldSendMailToSupplier: false },
                            });

                            if (!data) {
                                throw new Error("Une erreur est survenue lors du passage de l'achat en 'commandé'");
                            }

                            const {
                                markSupplyOrdersAsOrdered: { updatedSupplyOrders, errors, warnings },
                            } = data;
                            handleUpdateActionEndToast({
                                displaySuccess: !!updatedSupplyOrders.length,
                                successText: "L'achat a bien été marqué comme 'commandé'",
                                errors,
                                warnings,
                            });
                        }, "Souhaitez-vous vraiment marquer l'achat comme 'commandé' (sans envoi de mail automatique) ?");
                    },
                    disabled: SupplyOrderState.Ready !== state,
                },
            ],
        },
        {
            title: 'Prédictions',
            actions: [
                {
                    icon: <span>📊</span>,
                    label: 'Mettre à jour les variations de stock',
                    onClick: async () => {
                        await excecuteActionOnConfirm(async () => {
                            const { data, errors } = await updateManualSupplyOrderProductStockVariations();
                            handleUpdateResults({
                                data: data?.updateManualSupplyOrderProductStockVariations,
                                errors,
                                successMessage: 'Les variations de stock ont bien été mises à jour',
                                errorMessage: 'Erreur lors de la mise à jour des variations de stock',
                            });
                        }, 'Souhaitez-vous vraiment mettre à jour les variations de stock ?');
                    },
                },
                {
                    icon: <span>🔮</span>,
                    label: 'Mettre à jour les prédictions',
                    onClick: async () => {
                        await excecuteActionOnConfirm(async () => {
                            const { data, errors } = await updateManualSupplyOrderForecasts();
                            handleUpdateResults({
                                data: data?.updateManualSupplyOrderForecasts,
                                errors,
                                successMessage: 'Les prédictions ont bien été mises à jour',
                                errorMessage: 'Erreur lors de la mise à jour des prédictions',
                            });
                        }, 'Souhaitez-vous vraiment mettre à jour les prédictions ?');
                    },
                },
            ],
        },
    ];

    return (
        <SectionContainer title="Actions" isInitiallyOpen>
            <ActionContainer>
                <ActionButtons actionSections={actionSections} />
                {loading ? <FullPageLoader /> : null}
                {isDirty ? (
                    <DirtyOverlay>
                        <OverlayText>
                            Veuillez valider ou réinitialiser les changements en cours avant d'effectuer une action
                        </OverlayText>
                    </DirtyOverlay>
                ) : null}
            </ActionContainer>
        </SectionContainer>
    );
};

const ActionContainer = styled.div`
    position: relative;
`;

const DirtyOverlay = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${({ theme }) => theme.popupOverlayColor};
`;

const OverlayText = styled.div`
    color: ${({ theme }) => theme.ctaPrimaryColor};
    background-color: ${({ theme }) => theme.backgroundColor};
    padding: 5px;
`;
